// src/TranslationStatusPanel.tsx
// NOTE: Helper logic covered in frontend/src/__tests__/TranslationStatusPanel.test.tsx.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import type { TourProps } from 'antd';
import { resolveFrontendApiBase } from './utils/apiBase';
import axios from 'axios';
import {
  Button,
  Tabs,
  Tooltip,
  Empty,
  notification,
  Popconfirm,
  Modal,
  Select
} from 'antd';
import {
  SyncOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
  DeleteOutlined,
  ClearOutlined,
  InfoCircleOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useTranslationStatus } from './TranslationStatusContext';
import { NeverTranslateIssue, TranslationStatus } from './types';
import { getAuth } from 'firebase/auth';
import { triggerDownloadUrl } from './utils/download';
import { getAuthHeaders } from './utils/authHeaders';
import { splitLanguagePair } from './utils/languagePairs';
import AppTour, { renderAppTourPanel } from './tours/AppTour';
import { tourTarget, useGuidedTour } from './tours/useGuidedTour';
import './TranslationStatusPanel.css';

const API_BASE = resolveFrontendApiBase();
interface TranslationStatusPanelProps {
  bookId?: string;
  refreshDisabled?: boolean;
  triggerMode?: 'fixed' | 'inline';
  compactTrigger?: boolean;
  minimalTrigger?: boolean;
  openSignal?: number;
  tourUserId?: string;
}

type SortField = 'time' | 'chapterName' | 'type' | 'language';
type SortFieldSecondary = SortField | 'none';
type SortDirection = 'asc' | 'desc';
type SortTabKey = 'pending' | 'completed' | 'failed';
type ErrorActionLink = { label: string; href: string };

const normalizeStatus = (entry: TranslationStatus): { status: string; state: string } => {
  const status = (entry.status || '').trim().toLowerCase();
  const state = (entry.processingState || '').trim().toLowerCase();
  return { status, state };
};

const TERMINAL_STATES = new Set([
  'completed',
  'succeeded',
  'finished',
  'failed',
  'error',
  'expired',
  'interrupted',
  'released',
  'cancelled',
  'canceled',
  'aborted',
]);

export const resolveTranslationStatusState = (entry: TranslationStatus): string => {
  const { status, state } = normalizeStatus(entry);
  if (state && TERMINAL_STATES.has(status) && !TERMINAL_STATES.has(state)) {
    return status;
  }
  return state || status;
};

const SUCCESSFUL_STATES = new Set(['completed', 'succeeded', 'finished']);

export const isQueuedStatus = (entry: TranslationStatus): boolean => {
  const { status, state } = normalizeStatus(entry);
  return status === 'queued' || state === 'queued';
};

export const formatStatusErrorText = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    const rendered = value.map(formatStatusErrorText).filter(Boolean);
    return rendered.length ? rendered.join('\n') : '';
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['message', 'detail', 'error', 'description']) {
      const rendered = formatStatusErrorText(record[key]);
      if (rendered) {
        return rendered;
      }
    }
    try {
      return JSON.stringify(value);
    } catch (_err) {
      return String(value);
    }
  }
  return String(value);
};

export const getNeverTranslateRuleLinks = (entry: TranslationStatus): ErrorActionLink[] => {
  const errorKind = String(entry.errorKind || '').trim().toUpperCase();
  if (![
    'NEVER_TRANSLATE_VALIDATION',
    'NEVER_TRANSLATE_PRESERVATION_FAILED',
    'NEVER_TRANSLATE_RULE_INVALID',
    'NEVER_TRANSLATE_RULE_CASE_CONFLICT',
    'NEVER_TRANSLATE_CONTEXT_CONFLICT',
  ].includes(errorKind)) {
    return [];
  }

  const detail = formatStatusErrorText(entry.errorDetails).toLowerCase();
  const scopes = new Set<string>();
  (entry.neverTranslateIssues || []).forEach(issue => {
    (issue.scopes || []).forEach(scope => scopes.add(String(scope).toLowerCase()));
  });
  const scopePattern = /(?:scope|scopes)=([a-z,]+)/g;
  let scopeMatch: RegExpExecArray | null;
  while ((scopeMatch = scopePattern.exec(detail)) !== null) {
    (scopeMatch[1] || '').split(',').filter(Boolean).forEach(scope => scopes.add(scope));
  }
  const links: ErrorActionLink[] = [];
  if ((scopes.has('book') || scopes.has('effective')) && entry.bookId) {
    links.push({
      label: 'Review this book’s Never Translate rules',
      href: `/book-detail/${encodeURIComponent(entry.bookId)}`,
    });
  }
  if (scopes.has('series')) {
    links.push({
      label: 'Review series Never Translate rules',
      href: '/series/manage',
    });
  }
  if (scopes.has('account')) {
    links.push({
      label: 'Review account Never Translate rules',
      href: '/account/settings',
    });
  }
  if (!links.length && entry.bookId) {
    links.push({
      label: 'Review the Never Translate rules applied to this book',
      href: `/book-detail/${encodeURIComponent(entry.bookId)}`,
    });
  }
  return links;
};

export const getNeverTranslateIssues = (entry: TranslationStatus): NeverTranslateIssue[] => {
  const structured = (entry.neverTranslateIssues || []).filter(
    issue => Boolean(String(issue?.term || '').trim()),
  );
  if (structured.length) {
    return structured.slice(0, 6);
  }

  const errorKind = String(entry.errorKind || '').trim().toUpperCase();
  if (!errorKind.startsWith('NEVER_TRANSLATE_')) {
    return [];
  }
  const raw = `${formatStatusErrorText(entry.errorDetails)}\n${formatStatusErrorText(entry.error)}`;
  const issues: NeverTranslateIssue[] = [];
  const seen = new Set<string>();
  const termPattern = /term=("(?:\\.|[^"\\])*")(?=[\s;]|$)/g;
  let match: RegExpExecArray | null;
  while ((match = termPattern.exec(raw)) !== null && issues.length < 6) {
    try {
      const term = String(JSON.parse(match[1]) || '').trim();
      if (!term || seen.has(term)) continue;
      seen.add(term);
      issues.push({ term, scopes: ['effective'] });
    } catch (_err) {
      // Ignore malformed legacy diagnostics rather than showing technical text.
    }
  }
  if (!issues.length) {
    const rulesMatch = raw.match(/\brules=(\[[^\r\n]*?\])\s+(?=(?:scope|scopes)=)/);
    if (rulesMatch) {
      try {
        const rules = JSON.parse(rulesMatch[1]);
        if (Array.isArray(rules)) {
          rules.slice(0, 6).forEach(value => {
            const term = String(value || '').trim();
            if (term && !seen.has(term)) {
              seen.add(term);
              issues.push({ term, scopes: ['effective'], status: 'rule_configuration' });
            }
          });
        }
      } catch (_err) {
        // Ignore malformed legacy diagnostics.
      }
    }
  }
  return issues;
};

export const formatNeverTranslateIssueSummary = (entry: TranslationStatus): string => {
  const terms = getNeverTranslateIssues(entry).map(issue => issue.term);
  if (!terms.length) return '';
  const label = terms.length === 1
    ? 'Affected Never Translate rule'
    : 'Affected Never Translate rules';
  return `${label}: ${terms.map(term => `“${term}”`).join(', ')}`;
};

const normalizeMergeToken = (value: unknown): string =>
  String(value || '').trim().toLowerCase();

const getFrenchDialogueMergeKey = (entry: TranslationStatus): string => {
  const chapterGuid = normalizeMergeToken(entry.chapterGuid);
  if (chapterGuid) {
    return [
      'french-dialogue',
      normalizeMergeToken(entry.bookId),
      chapterGuid,
    ].join('|');
  }
  return [
    'french-dialogue',
    normalizeMergeToken(entry.bookId),
    normalizeMergeToken(entry.chapterName),
    normalizeMergeToken(entry.sourceLang),
    normalizeMergeToken(entry.targetLang),
  ].join('|');
};

const getLogicalMergeKey = (entry: TranslationStatus): string => {
  if (normalizeMergeToken(entry.type) === 'frenchdialoguecorrection') {
    return getFrenchDialogueMergeKey(entry);
  }
  return [
    'composite',
    normalizeMergeToken(entry.bookId),
    normalizeMergeToken(entry.type),
    normalizeMergeToken(entry.chapterGuid),
    normalizeMergeToken(entry.sourceLang),
    normalizeMergeToken(entry.targetLang),
  ].join('|');
};

const getMergeKey = (entry: TranslationStatus): string => {
  const typeKey = normalizeMergeToken(entry.type);
  if (typeKey === 'frenchdialoguecorrection') {
    return getLogicalMergeKey(entry);
  }
  const lockGuid = normalizeMergeToken(entry.lockGuid);
  if (lockGuid) return `lock:${lockGuid}`;
  const lockKey = normalizeMergeToken(entry.lockKey);
  if (lockKey) return `lock:${lockKey}`;
  return getLogicalMergeKey(entry);
};

const statePriority = (entry: TranslationStatus): number => {
  const resolved = resolveTranslationStatusState(entry);
  if (resolved === 'failed') return 5;
  if (resolved === 'completed') return 4;
  if (resolved === 'running' || resolved === 'retrying') return 3;
  if (resolved === 'queued') return 2;
  return 1;
};

const shouldReplaceExisting = (candidate: TranslationStatus, current: TranslationStatus): boolean => {
  const candidateType = normalizeMergeToken(candidate.type);
  const currentType = normalizeMergeToken(current.type);
  if (candidateType === 'frenchdialoguecorrection' && currentType === 'frenchdialoguecorrection') {
    const candidateInProgress = isQueuedStatus(candidate) || ['pending', 'running', 'retrying'].includes(resolveTranslationStatusState(candidate));
    const currentInProgress = isQueuedStatus(current) || ['pending', 'running', 'retrying'].includes(resolveTranslationStatusState(current));
    const candidateCompleted = SUCCESSFUL_STATES.has(resolveTranslationStatusState(candidate));
    const currentCompleted = SUCCESSFUL_STATES.has(resolveTranslationStatusState(current));
    const candidateFailed = resolveTranslationStatusState(candidate) === 'failed';
    const currentFailed = resolveTranslationStatusState(current) === 'failed';
    if (candidateCompleted && currentFailed) {
      return true;
    }
    if (candidateFailed && currentCompleted) {
      return false;
    }
    if (candidateInProgress && currentFailed) {
      return true;
    }
    if (candidateFailed && currentInProgress) {
      return false;
    }
  }

  const candidateClient = Boolean(candidate.clientIntent);
  const currentClient = Boolean(current.clientIntent);
  if (candidateClient !== currentClient) {
    return !candidateClient;
  }

  const candidateStatePriority = statePriority(candidate);
  const currentStatePriority = statePriority(current);
  if (candidateStatePriority !== currentStatePriority) {
    return candidateStatePriority > currentStatePriority;
  }

  const candidateTime = new Date(candidate.completionTime || candidate.timestamp || 0).getTime();
  const currentTime = new Date(current.completionTime || current.timestamp || 0).getTime();
  return candidateTime >= currentTime;
};

const TranslationStatusPanel: React.FC<TranslationStatusPanelProps> = ({
  bookId,
  refreshDisabled = false,
  triggerMode = 'fixed',
  compactTrigger = false,
  minimalTrigger = false,
  openSignal,
  tourUserId,
}) => {
  const auth = getAuth();
  const {
    translations: allTranslations,
    refreshTranslations: onRefresh,
    refreshTranslationsImmediate: onRefreshImmediate,
    realtimeConnected,
    realtimeError,
    sortConfig,
    updateSortConfig,
    clientIntents,
  } = useTranslationStatus();

  const isInProgress = (entry: TranslationStatus): boolean => {
    const effectiveState = resolveTranslationStatusState(entry);
    return ['pending', 'queued', 'running', 'retrying'].includes(effectiveState);
  };

  const isCompletedEntry = (entry: TranslationStatus): boolean => {
    return SUCCESSFUL_STATES.has(resolveTranslationStatusState(entry));
  };

  const isFailedEntry = (entry: TranslationStatus): boolean => {
    return resolveTranslationStatusState(entry) === 'failed';
  };

  // Mirror of context translations for optimistic updates
  const [localTranslations, setLocalTranslations] = useState<TranslationStatus[]>(allTranslations);
  const clearedLockIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (clearedLockIdsRef.current.size === 0) {
      setLocalTranslations(allTranslations);
      return;
    }
    setLocalTranslations(
      allTranslations.filter(entry => {
        const lockId = entry.lockGuid || entry.lockKey || '';
        return !clearedLockIdsRef.current.has(lockId);
      })
    );
  }, [allTranslations]);

  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'pending' | 'completed' | 'failed'>('pending');
  const [clearingItem, setClearingItem] = useState<string | null>(null);
  const [isClearingAll, setIsClearingAll] = useState<boolean>(false);
  const [clearAllModalVisible, setClearAllModalVisible] = useState<'completed' | 'failed' | null>(null);
  const [errorModalVisible, setErrorModalVisible] = useState<boolean>(false);
  const [currentError, setCurrentError] = useState<string | undefined>(undefined);
  const [currentErrorLinks, setCurrentErrorLinks] = useState<ErrorActionLink[]>([]);
  const [currentCodeValue, setCurrentCodeValue] = useState<string | undefined>(undefined);
  const [currentCodeLabel, setCurrentCodeLabel] = useState<'Support code' | 'Reference ID'>('Support code');
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [refreshingExportLockKey, setRefreshingExportLockKey] = useState<string | null>(null);
  const processingTour = useGuidedTour({
    userId: tourUserId || auth.currentUser?.uid,
    apiBase: API_BASE,
    flag: 'processingStatusPanelTourSeen',
    enabled: isVisible,
    autoStart: isVisible,
  });
  const processingTourSteps = useMemo<TourProps['steps']>(() => ([
    {
      title: 'Open processing status',
      description: renderAppTourPanel('This button opens live progress for translations, proofreads, audiobook jobs, exports, and related background tasks.'),
      target: tourTarget('processing-status-trigger', 'processing-status'),
    },
    {
      title: 'Review active work',
      description: renderAppTourPanel('In-process jobs show type, language, start time, and any waiting state such as queued character analysis.'),
      target: tourTarget('processing-status-tabs', 'processing-status'),
    },
    {
      title: 'Use the status tabs',
      description: renderAppTourPanel('Switch between in-process, completed, and failed jobs. Each tab can be sorted independently.'),
      target: tourTarget('processing-status-tabs', 'processing-status'),
    },
    {
      title: 'Refresh and clear notifications',
      description: renderAppTourPanel('Refresh active jobs or remove completed and failed notifications once you have reviewed them.'),
      target: tourTarget('processing-status-actions', 'processing-status'),
    },
    {
      title: 'Open failure details',
      description: renderAppTourPanel('Failed rows include friendly next steps and a support code when support needs to investigate.'),
      target: tourTarget('processing-status-failed-list', 'processing-status-tabs'),
    },
  ]), []);

  const lastRefreshTimeRef = useRef<number>(0);
  const prevTranslationsRef = useRef<TranslationStatus[]>([]);
  const autoOpenInitializedRef = useRef(false);
  const lastOpenSignalRef = useRef<number | undefined>(openSignal);
  const autoOpenTranslations = useMemo(() => {
    const merged = clientIntents.length === 0
      ? localTranslations
      : [...clientIntents, ...localTranslations];
    if (!bookId) {
      return merged;
    }
    return merged.filter(entry => entry.bookId === bookId);
  }, [clientIntents, localTranslations, bookId]);

  useEffect(() => {
    if (!realtimeError) return;
    notification.warning({
      message: 'Live update connection lost',
      description: realtimeError,
      placement: 'bottomRight',
      duration: 4,
    });
  }, [realtimeError]);

  useEffect(() => {
    if (openSignal == null || openSignal === lastOpenSignalRef.current) {
      return;
    }
    lastOpenSignalRef.current = openSignal;
    setActiveTab('pending');
    setIsVisible(true);
  }, [openSignal]);

  const DEFAULT_ERROR_FALLBACK = `The task ended unexpectedly.

Next steps:
- Click Refresh in Processing Status and try again.
- Try a different AI model in the Translate dialog.
- Translate a single chapter to isolate issues.`;

  const describeProvider = (provider?: string): string => {
    const normalized = (provider || '').trim().toLowerCase();
    if (!normalized) return 'the AI model';
    if (normalized === 'openai') return 'OpenAI';
    if (normalized === 'gemini') return 'Gemini';
    return (provider || '').trim();
  };

  const buildChunkValidationMessage = (entry: TranslationStatus): string => {
    const providerDisplay = describeProvider(entry.batchFailureProvider);
    const intro = `We stopped this translation because ${providerDisplay} returned output that failed our quality checks.`;
    const suggestions = `\n\nWhat you can try next:\n- Retry this chapter; most runs pass on the second attempt.\n- If it keeps failing, translate the chapter without batching or choose a different AI model.\n- If it still fails, contact support with the Support Code shown.`;
    return `${intro}${suggestions}`;
  };

  const buildFrenchDialogueFailureMessage = (entry: TranslationStatus): string => {
    const technicalDetail = (
      formatStatusErrorText(entry.errorDetails)
      || formatStatusErrorText(entry.error)
      || formatStatusErrorText(entry.statusMessage)
    ).trim();
    const lowered = technicalDetail.toLowerCase();

    if (lowered.includes('replacement_validation_failed')) {
      return `We couldn't complete the French dialogue update for this chapter.

What you can try next:
- Click Retry on this failed chapter.
- If it fails again, refresh the page and run Apply Updated French Dialog Rules again.
- If it still fails, contact support with the Support Code shown.`;
    }

    if (lowered.includes('changed after queueing')) {
      return `This chapter changed before the French dialogue update finished, so we stopped to avoid overwriting newer text.

What you can try next:
- Refresh the page.
- Click Retry on this failed chapter.
- If it still fails, contact support with the Support Code shown.`;
    }

    if (lowered.includes('translated chapter not found') || lowered.includes('missing source html')) {
      return `We couldn't load the chapter data needed to apply the French dialogue update.

What you can try next:
- Refresh the page and click Retry on this failed chapter.
- If the chapter was recently edited, wait a moment and try again.
- If it still fails, contact support with the Support Code shown.`;
    }

    if (lowered.includes('supports english->french') || lowered.includes('unsupported_pair')) {
      return `French dialogue cleanup currently supports English to French translations only.`;
    }

    return `We couldn't apply the French dialogue update to this chapter.

What you can try next:
- Click Retry on this failed chapter.
- If it keeps failing, refresh the page and run Apply Updated French Dialog Rules again.
- If it still fails, contact support with the Support Code shown.`;
  };

  const sanitizeCharacterAnalysisStatusMessage = (message: string): string => {
    const trimmed = (message || '').trim();
    if (!trimmed) {
      return '';
    }
    const lowered = trimmed.toLowerCase();
    const leaksInternalDetail =
      lowered.includes('api returned status') ||
      lowered.includes('openrouter') ||
      lowered.includes('user not found') ||
      lowered.includes('first error:') ||
      lowered.includes('{"error"') ||
      lowered.includes('code 401') ||
      lowered.includes('code 403');
    if (leaksInternalDetail) {
      return 'Character relationship analysis failed. Please retry Translate Book. If this keeps failing, contact support.';
    }
    return trimmed;
  };

  const getCharacterAnalysisStatusMessage = (entry: TranslationStatus): string => {
    const rawMessage = (entry.statusMessage || '').trim();
    if (!entry.blockedByCharacterAnalysis) {
      return rawMessage;
    }
    const status = (entry.characterAnalysisStatus || '').trim().toLowerCase();
    if (status === 'failed') {
      if (rawMessage) {
        return sanitizeCharacterAnalysisStatusMessage(rawMessage);
      }
      return 'Character relationship analysis failed. Please retry Translate Book.';
    }
    return rawMessage || 'Waiting for character relationship analysis.';
  };

  const resolveErrorMessageForEntry = (entry: TranslationStatus): string => {
    if (entry.type === 'FrenchDialogueCorrection') {
      return buildFrenchDialogueFailureMessage(entry);
    }

    const code = (entry.errorCode || formatStatusErrorText(entry.error)).trim();
    if (code === 'chunk_validation_failed') {
      return buildChunkValidationMessage(entry);
    }

    // Sanitize the friendly error by removing "Details: " and everything after
    const rawFriendly = formatStatusErrorText(entry.error);
    const friendly = rawFriendly.replace(/Details:\s*[\s\S]*$/,'').trim();
    const affectedRuleSummary = formatNeverTranslateIssueSummary(entry);

    if (friendly) {
      return affectedRuleSummary && !friendly.includes(affectedRuleSummary)
        ? `${friendly}\n\n${affectedRuleSummary}`
        : friendly;
    }
    const details = formatStatusErrorText(entry.errorDetails);
    if (details) {
      return affectedRuleSummary || details;
    }
    return DEFAULT_ERROR_FALLBACK;
  };

  const alreadyMentionsNoCharge = (text: string): boolean => {
    const lowered = (text || '').toLowerCase();
    return lowered.includes('not charged') || lowered.includes('no credits were deducted') || lowered.includes('refunded');
  };

  const appendBillingNote = (message: string, entry: TranslationStatus): string => {
    const safeMessage = message || '';
    const status = (entry.creditChargeStatus || '').toLowerCase();
    const isProofreadFailure = (entry.type || '').trim().toLowerCase() === 'proofread' && isFailedEntry(entry);
    const proofreadFailureNote = 'No credits were deducted for this failed attempt. Retrying may show a credit estimate because a successful new attempt is billed normally.';
    if (status === 'not_charged' || status === 'not-charged') {
      const note = 'You were not charged for this failed attempt.';
      return alreadyMentionsNoCharge(safeMessage) || safeMessage.includes(note) ? safeMessage : `${safeMessage}\n\n${note}`;
    }
    if (status === 'refunded') {
      const note = 'We already refunded the credits for this attempt.';
      if (safeMessage.includes(note) || safeMessage.toLowerCase().includes('refunded')) {
        return safeMessage;
      }
      return `${safeMessage}\n\n${note}`;
    }
    if (isProofreadFailure && (status === '' || status === 'pending') && !alreadyMentionsNoCharge(safeMessage)) {
      return `${safeMessage}\n\n${proofreadFailureNote}`;
    }
    if ((status === '' || status === 'pending') && isFailedEntry(entry) && !alreadyMentionsNoCharge(safeMessage)) {
      const note = 'Failed chapters are not charged.';
      return `${safeMessage}\n\n${note}`;
    }
    return safeMessage;
  };

  const showErrorDetails = (entry: TranslationStatus) => {
    const message = resolveErrorMessageForEntry(entry);
    setCurrentError(appendBillingNote(message, entry));
    setCurrentErrorLinks(getNeverTranslateRuleLinks(entry));
    const supportCode = (entry.supportCode || '').trim().toUpperCase();
    const referenceId = (entry.lockGuid || entry.lockKey || '').trim();
    if (supportCode) {
      setCurrentCodeLabel('Support code');
      setCurrentCodeValue(supportCode);
    } else if (referenceId) {
      setCurrentCodeLabel('Reference ID');
      setCurrentCodeValue(referenceId);
    } else {
      setCurrentCodeLabel('Support code');
      setCurrentCodeValue(undefined);
    }
    setErrorModalVisible(true);
  };

  const handleCloseErrorModal = () => {
    setErrorModalVisible(false);
    setCurrentErrorLinks([]);
    setCurrentError(undefined);
    setCurrentCodeValue(undefined);
    setCurrentCodeLabel('Support code');
  };

  const copyValueToClipboard = async (value: string, label: string) => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        notification.success({
          message: `${label} copied`,
          placement: 'bottomRight',
          duration: 2,
        });
      } else {
        throw new Error('Clipboard API unavailable');
      }
    } catch (copyError: unknown) {
      console.error(`Failed to copy ${label}`, copyError);
      notification.error({
        message: `Unable to copy ${label.toLowerCase()}`,
        placement: 'bottomRight',
        duration: 2,
      });
    }
  };

  const handleCopySupportCode = () => {
    if (currentCodeValue) {
      copyValueToClipboard(currentCodeValue, currentCodeLabel);
    }
  };

  const getAudiobookVersion = (entry: TranslationStatus): string => {
    const explicitVersion = [entry.version, entry.targetLang]
      .find(value => typeof value === 'string' && value.trim());
    if (explicitVersion) {
      return explicitVersion.trim();
    }

    // Compatibility for audiobook jobs mirrored before `version` was included in
    // userActiveJobs. Chapter lock IDs end in `_{chapterGuid}_{version}`.
    const lockId = (entry.lockKey || entry.lockGuid || '').trim();
    const chapterGuid = (entry.chapterGuid || '').trim();
    if (lockId && chapterGuid) {
      const chapterMarker = `_${chapterGuid}_`;
      const markerIndex = lockId.indexOf(chapterMarker);
      if (markerIndex >= 0) {
        return lockId.slice(markerIndex + chapterMarker.length).trim();
      }
    }

    // Intro/outro lock IDs use `{bookId}_{version}_{section}`.
    const bookId = (entry.bookId || '').trim();
    const section = (entry.chapterName || '').trim();
    if (lockId && bookId && /^(intro|outro)$/i.test(section)) {
      const prefix = `${bookId}_`;
      const suffix = `_${section}`;
      if (
        lockId.startsWith(prefix)
        && lockId.toLowerCase().endsWith(suffix.toLowerCase())
      ) {
        return lockId.slice(prefix.length, lockId.length - suffix.length).trim();
      }
    }

    return typeof entry.sourceLang === 'string' ? entry.sourceLang.trim() : '';
  };

  // Helper function to extract and format target language for AudioBookGeneration
  const getAudiobookDisplayLanguage = (entry: TranslationStatus): string => {
    const version = getAudiobookVersion(entry);
    if (!version) return 'Unknown Language';

    const targetLang = splitLanguagePair(version).targetLang || version;

    // Apply initial capitalization regardless of format
    return targetLang.charAt(0).toUpperCase() + targetLang.slice(1).toLowerCase();
  };

  const getExportDisplayLabel = (entry: TranslationStatus): string => {
    const versionLabel = (entry.version || 'Export').trim();
    const formatLabel = (entry.format || '').trim();
    if (!formatLabel) {
      return versionLabel;
    }
    return `${versionLabel} (${formatLabel})`;
  };
  const isRelationshipAnalysis = (entry: TranslationStatus): boolean =>
    (entry.type || '').trim().toLowerCase() === 'relationship analysis';
  const relationshipAnalysisLabel = 'Book-wide';

  const getProofreadDisplayLabel = (entry: TranslationStatus): string => {
    const version = String(entry.targetLang || '').trim();
    if (!version) {
      return '';
    }
    if (version.toLowerCase() === 'original') {
      return 'Original';
    }
    const { sourceLang, targetLang } = splitLanguagePair(version);
    if (sourceLang && targetLang) {
      return `${sourceLang} → ${targetLang}`;
    }
    return version;
  };

  const getEntryLanguageTooltip = (entry: TranslationStatus): string => {
    if (entry.type === 'AudioBookGeneration') {
      return `Language: ${getAudiobookDisplayLanguage(entry)}`;
    }
    if (entry.type === 'Export') {
      return `Export: ${getExportDisplayLabel(entry)}`;
    }
    if (isRelationshipAnalysis(entry)) {
      return 'Book-wide analysis';
    }
    if (entry.type === 'Proofread' || entry.type === 'ProofreadSubmission') {
      return `Proofread: ${getProofreadDisplayLabel(entry)}`;
    }
    return `From ${entry.sourceLang} to ${entry.targetLang}`;
  };

  const getEntryLanguageLabel = (entry: TranslationStatus): string => {
    if (entry.type === 'AudioBookGeneration') {
      return getAudiobookDisplayLanguage(entry);
    }
    if (entry.type === 'Export') {
      return getExportDisplayLabel(entry);
    }
    if (isRelationshipAnalysis(entry)) {
      return relationshipAnalysisLabel;
    }
    if (entry.type === 'Proofread' || entry.type === 'ProofreadSubmission') {
      return getProofreadDisplayLabel(entry);
    }
    return `${entry.sourceLang} → ${entry.targetLang}`;
  };

  const sortOptions: { value: SortField; label: string }[] = [
    { value: 'time', label: 'Time' },
    { value: 'chapterName', label: 'Chapter' },
    { value: 'type', label: 'Type' },
    { value: 'language', label: 'Language' },
  ];
  const secondarySortOptions: { value: SortFieldSecondary; label: string }[] = [
    { value: 'none', label: 'None' },
    ...sortOptions,
  ];

  // Throttled manual refresh
  const handleRefresh = async () => {
    if (refreshDisabled) {
      notification.info({
        message: 'Please wait',
        description: 'We’re preparing translations. Refresh will be available shortly.',
        placement: 'bottomRight',
        duration: 2,
      });
      return;
    }
    const now = Date.now();
    if (now - lastRefreshTimeRef.current < 10_000) {
      notification.info({
        message: 'Please wait',
        description: 'Refreshing too frequently, please wait a moment',
        placement: 'bottomRight',
        duration: 2,
      });
      return;
    }
    setIsRefreshing(true);
    lastRefreshTimeRef.current = now;
    try {
      await onRefreshImmediate(true);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Auto-open panel only for user-initiated work (client intents).
  useEffect(() => {
    if (!autoOpenInitializedRef.current) {
      prevTranslationsRef.current = [...autoOpenTranslations];
      autoOpenInitializedRef.current = true;
      return;
    }
    if (autoOpenTranslations.length === 0) {
      prevTranslationsRef.current = [];
      return;
    }
    const prevIds = new Set(prevTranslationsRef.current.map(t => t.lockGuid));
    const hasNewClientIntent = autoOpenTranslations.some(
      t => t.clientIntent && !prevIds.has(t.lockGuid)
    );
    if (hasNewClientIntent && !isVisible) {
      setIsVisible(true);
      setActiveTab('pending');
    }
    prevTranslationsRef.current = [...autoOpenTranslations];
  }, [autoOpenTranslations, isVisible]);

  // Helpers for time formatting, tags, etc...
  const parseUTCTimestamp = (s: string): Date => {
    if (!s) return new Date(NaN);
    // If string already includes a timezone designator (Z or +HH:MM/-HH:MM),
    // use it as-is; otherwise, assume UTC and append 'Z'.
    const hasTZ = /[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s);
    const str = hasTZ ? s : `${s}Z`;
    const d = new Date(str);
    return isNaN(d.getTime()) ? new Date(s) : d;
  };
  const formatRelativeTime = (s: string): string => {
    const date = parseUTCTimestamp(s);
    const diffMs = Date.now() - date.getTime();
    const sec = Math.round(diffMs / 1000);
    const min = Math.round(sec / 60);
    const hr = Math.round(min / 60);
    const day = Math.round(hr / 24);
    if (sec < 5) return 'just now';
    if (sec < 60) return `${sec} seconds ago`;
    if (min < 60) return `${min} ${min === 1 ? 'minute' : 'minutes'} ago`;
    if (hr < 24) return `${hr} ${hr === 1 ? 'hour' : 'hours'} ago`;
    if (day < 30) return `${day} ${day === 1 ? 'day' : 'days'} ago`;
    return `on ${date.toLocaleDateString()}`;
  };
  const getTimeElapsed = (s: string) => formatRelativeTime(s);

  const getSortTime = (entry: TranslationStatus, tabKey: SortTabKey): number => {
    const timeSource = tabKey === 'pending'
      ? entry.timestamp
      : (entry.completionTime || entry.timestamp);
    return parseUTCTimestamp(timeSource).getTime();
  };

  const getSortValue = (entry: TranslationStatus, field: SortField, tabKey: SortTabKey): number | string => {
    switch (field) {
      case 'time':
        return getSortTime(entry, tabKey);
      case 'chapterName':
        return (entry.chapterName || '').toLowerCase();
      case 'type':
        return (entry.type || '').toLowerCase();
      case 'language':
        if (entry.type === 'AudioBookGeneration') {
          return getAudiobookDisplayLanguage(entry).toLowerCase();
        }
        if (entry.type === 'Export') {
          return getExportDisplayLabel(entry).toLowerCase();
        }
        if (isRelationshipAnalysis(entry)) {
          return relationshipAnalysisLabel.toLowerCase();
        }
        return `${entry.sourceLang || ''}-${entry.targetLang || ''}`.toLowerCase();
      default:
        return getSortTime(entry, tabKey);
    }
  };

  const collator = useMemo(
    () => new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }),
    []
  );

  const sortTranslations = (
    entries: TranslationStatus[],
    config: {
      primaryField: SortField;
      secondaryField: SortFieldSecondary;
      primaryDirection: SortDirection;
      secondaryDirection: SortDirection;
    },
    tabKey: SortTabKey
  ): TranslationStatus[] => {
    if (entries.length < 2) return entries;
    return entries
      .map((entry, index) => ({ entry, index }))
      .sort((a, b) => {
        const valueA = getSortValue(a.entry, config.primaryField, tabKey);
        const valueB = getSortValue(b.entry, config.primaryField, tabKey);
        const primaryMultiplier = config.primaryDirection === 'asc' ? 1 : -1;
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          const diff = valueA - valueB;
          if (diff !== 0) {
            return diff * primaryMultiplier;
          }
        } else {
          const diff = collator.compare(String(valueA), String(valueB));
          if (diff !== 0) {
            return diff * primaryMultiplier;
          }
        }

        if (config.secondaryField !== 'none') {
          const secondaryA = getSortValue(a.entry, config.secondaryField, tabKey);
          const secondaryB = getSortValue(b.entry, config.secondaryField, tabKey);
          const secondaryMultiplier = config.secondaryDirection === 'asc' ? 1 : -1;
          if (typeof secondaryA === 'number' && typeof secondaryB === 'number') {
            const secondaryDiff = secondaryA - secondaryB;
            if (secondaryDiff !== 0) {
              return secondaryDiff * secondaryMultiplier;
            }
          } else {
            const secondaryDiff = collator.compare(String(secondaryA), String(secondaryB));
            if (secondaryDiff !== 0) {
              return secondaryDiff * secondaryMultiplier;
            }
          }
        }

        return a.index - b.index;
      })
      .map(({ entry }) => entry);
  };

  const renderSortControls = (tabKey: SortTabKey) => {
    const config = sortConfig[tabKey];
    return (
      <div className="processing-status-sort-shell">
        <div className="processing-status-sort-bar">
          <span className="processing-status-sort-label">
            Sort
          </span>
          <div className="processing-status-sort-control">
            <Select
              size="small"
              value={config.primaryField}
              onChange={value => updateSortConfig(tabKey, { primaryField: value as SortField })}
              className="processing-status-sort-select"
              options={sortOptions}
            />
            <Tooltip title={config.primaryDirection === 'asc' ? 'Ascending' : 'Descending'}>
              <Button
                size="small"
                type="text"
                icon={config.primaryDirection === 'asc' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                style={{ padding: '0 2px' }}
                onClick={() =>
                  updateSortConfig(tabKey, {
                    primaryDirection: config.primaryDirection === 'asc' ? 'desc' : 'asc',
                  })
                }
              />
            </Tooltip>
          </div>
          <span className="processing-status-sort-label processing-status-sort-label--secondary">
            Then
          </span>
          <div className="processing-status-sort-control">
            <Select
              size="small"
              value={config.secondaryField}
              onChange={value => updateSortConfig(tabKey, { secondaryField: value as SortFieldSecondary })}
              className="processing-status-sort-select"
              options={secondarySortOptions.filter(option => option.value === 'none' || option.value !== config.primaryField)}
            />
            <Tooltip title={config.secondaryDirection === 'asc' ? 'Ascending' : 'Descending'}>
              <Button
                size="small"
                type="text"
                disabled={config.secondaryField === 'none'}
                icon={config.secondaryDirection === 'asc' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                style={{ padding: '0 2px' }}
                onClick={() =>
                  updateSortConfig(tabKey, {
                    secondaryDirection: config.secondaryDirection === 'asc' ? 'desc' : 'asc',
                  })
                }
              />
            </Tooltip>
          </div>
        </div>
      </div>
    );
  };

  const estimatedCompletion = (t: TranslationStatus): string => {
    const m = t.estimatedTimeMinutes || 0;
    if (m <= 0) return 'in < 1 minute';
    if (m < 60) return `in ${m} ${m === 1 ? 'minute' : 'minutes'}`;
    const h = Math.floor(m / 60);
    const r = m % 60;
    if (r === 0) return `in ${h} ${h === 1 ? 'hour' : 'hours'}`;
    return `in ${h} ${h === 1 ? 'hour' : 'hours'}, ${r} ${r === 1 ? 'minute' : 'minutes'}`;
  };

  const getTypeTag = (type: string) => {
    const tone =
      type === 'Translation' ? 'translation' :
      (type === 'Proofread' || type === 'ProofreadSubmission') ? 'proofread' :
      type === 'EditingNotes' ? 'notes' :
      type === 'AudioBookGeneration' ? 'audiobook' :
      type === 'Marketing Inspiration' ? 'marketing' :
      type === 'FrenchDialogueCorrection' ? 'french-dialogue' :
      type === 'Export' ? 'export' :
      'default';
    const text =
      type === 'ProofreadSubmission' ? 'Whole-book proofreading' :
      type === 'EditingNotes' ? 'Editing Notes' :
      type === 'AudioBookGeneration' ? 'Audiobook' :
      type === 'Marketing Inspiration' ? 'Inspiration' :
      type === 'FrenchDialogueCorrection' ? 'French Dialogue' :
      type;
    return <span className={`processing-status-type-pill processing-status-type-pill--${tone}`}>{text}</span>;
  };

  const handleExportDownload = async (entry: TranslationStatus) => {
    const lockKey = entry.lockKey || entry.lockGuid;
    if (!lockKey) {
      notification.error({
        message: 'Missing export identifier',
        placement: 'bottomRight',
      });
      return;
    }
    if (!auth.currentUser) {
      notification.error({
        message: 'No user logged in',
        placement: 'bottomRight',
      });
      return;
    }
    setRefreshingExportLockKey(lockKey);
    try {
      let targetUrl = entry.downloadUrl || null;
      const authHeaders = await getAuthHeaders(auth.currentUser);
      if (targetUrl) {
        try {
          const parsedUrl = new URL(targetUrl, window.location.origin);
          if (!parsedUrl.pathname.endsWith('/export-download-proxy')) {
            const headResponse = await fetch(targetUrl, { method: 'HEAD' });
            if (!headResponse.ok) {
              targetUrl = null;
            }
          }
        } catch {
          targetUrl = null;
        }
      }
      if (!targetUrl) {
        const response = await axios.post(`${API_BASE}/export-download-url`, {
          user_id: auth.currentUser.uid,
          lock_key: lockKey,
        }, { headers: authHeaders });
        targetUrl = response?.data?.downloadUrl || null;
      }
      if (!targetUrl) {
        throw new Error('No download URL available');
      }
      const parsedUrl = new URL(targetUrl, window.location.origin);
      if (parsedUrl.pathname.endsWith('/export-download-proxy')) {
        const response = await fetch(targetUrl, { headers: authHeaders });
        if (!response.ok) {
          throw new Error(`Export proxy download failed with status ${response.status}`);
        }
        const blobUrl = window.URL.createObjectURL(await response.blob());
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = entry.downloadFilename || 'export';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else {
        triggerDownloadUrl(targetUrl);
      }
      onRefreshImmediate(true);
    } catch (error) {
      console.error('Failed to refresh export link:', error);
      notification.error({
        message: 'Unable to download export',
        description: 'Please try again in a moment.',
        placement: 'bottomRight',
      });
    } finally {
      setRefreshingExportLockKey(null);
    }
  };

  const renderExportDownload = (entry: TranslationStatus) => {
    if (entry.type !== 'Export') {
      return null;
    }
    return (
      <Button
        type="link"
        size="small"
        onClick={() => handleExportDownload(entry)}
        disabled={refreshingExportLockKey === (entry.lockKey || entry.lockGuid)}
      >
        Download
      </Button>
    );
  };

  // Optimistic single-clear
  const clearTranslation = async (lockId: string) => {
    clearedLockIdsRef.current.add(lockId);
    setClearingItem(lockId);
    // immediately remove from UI
    setLocalTranslations(prev => prev.filter(t => (t.lockGuid || t.lockKey) !== lockId));
    try {
      await axios.post(
        `${API_BASE}/translationlock/clear`,
        { lockGuid: lockId }
      );
      // sync with server
      onRefresh();
    } catch {
      notification.error({
        message: 'Failed to clear',
        description: 'There was an error clearing the translation entry',
        placement: 'bottomRight'
      });
      // roll back UI removal if you want: onRefresh() will repopulate
    } finally {
      setClearingItem(null);
    }
  };

  const resolveLockIdentifier = (entry: TranslationStatus): string | null =>
    entry.lockGuid || entry.lockKey || null;

  // Bulk-clear with loading indicator
  const clearAllTranslations = async (status: 'completed' | 'failed') => {
    const toDelete = localTranslations
      .filter(t => t.status === status && !t.cleared);
    if (toDelete.length === 0) {
      notification.info({
        message: 'Nothing to clear',
        description: 'There are no entries to clear',
        placement: 'bottomRight'
      });
      setClearAllModalVisible(null);
      return;
    }

    setIsClearingAll(true);
    toDelete.forEach(entry => {
      const lockId = resolveLockIdentifier(entry);
      if (lockId) {
        clearedLockIdsRef.current.add(lockId);
      }
    });
    // optimistic removal
    setLocalTranslations(prev =>
      prev.filter(t => !(t.status === status && !t.cleared))
    );

    try {
      await axios.post(
        `${API_BASE}/translationlock/clear-all`,
        { lockGuids: toDelete.map(resolveLockIdentifier).filter(Boolean) }
      );
      onRefresh();
    } catch {
      notification.error({
        message: 'Failed to clear',
        description: `There was an error clearing the ${status} entries`,
        placement: 'bottomRight'
      });
    } finally {
      setIsClearingAll(false);
      setClearAllModalVisible(null);
    }
  };


  // Derive filtered lists from our local copy + client intents
  const mergedTranslations = useMemo(() => {
    const merged = [...clientIntents, ...localTranslations];
    const byKey = new Map<string, TranslationStatus>();
    merged.forEach(entry => {
      const key = getMergeKey(entry);
      const existing = byKey.get(key);
      if (!existing || shouldReplaceExisting(entry, existing)) {
        byKey.set(key, entry);
      }
    });
    const deduped = Array.from(byKey.values());
    const activeFrenchDialogueKeys = new Set(
      deduped
        .filter(entry => {
          const typeKey = normalizeMergeToken(entry.type);
          return typeKey === 'frenchdialoguecorrection' && isInProgress(entry);
        })
        .map(getLogicalMergeKey)
    );

    return deduped.filter(entry => {
      const typeKey = normalizeMergeToken(entry.type);
      if (typeKey !== 'frenchdialoguecorrection') {
        return true;
      }
      if (!isFailedEntry(entry)) {
        return true;
      }
      return !activeFrenchDialogueKeys.has(getLogicalMergeKey(entry));
    });
  }, [clientIntents, localTranslations]);

  const filtered = useMemo(
    () => (bookId ? mergedTranslations.filter(t => t.bookId === bookId) : mergedTranslations),
    [bookId, mergedTranslations]
  );
  const pendingRaw = useMemo(() => filtered.filter(isInProgress), [filtered]);
  const completedRaw = useMemo(
    () => filtered.filter(t => isCompletedEntry(t) && !t.cleared),
    [filtered]
  );
  const failedRaw = useMemo(
    () => filtered.filter(t => isFailedEntry(t) && !t.cleared),
    [filtered]
  );
  const pending = useMemo(
    () => sortTranslations(pendingRaw, sortConfig.pending, 'pending'),
    [pendingRaw, sortConfig.pending]
  );
  const completed = useMemo(
    () => sortTranslations(completedRaw, sortConfig.completed, 'completed'),
    [completedRaw, sortConfig.completed]
  );
  const failed = useMemo(
    () => sortTranslations(failedRaw, sortConfig.failed, 'failed'),
    [failedRaw, sortConfig.failed]
  );

  const totalCount = pending.length + completed.length + failed.length;
  const pendingTranslations = pending.filter(t => t.type === 'Translation').length;
  const pendingProofreading = pending.filter(t => t.type === 'Proofread' || t.type === 'ProofreadSubmission').length;
  const pendingEditingNotes = pending.filter(t => t.type === 'EditingNotes').length;
  const pendingAudiobooks = pending.filter(t => t.type === 'AudioBookGeneration').length;
  const pendingExports = pending.filter(t => t.type === 'Export').length;
  const pendingMarketingInspiration = pending.filter(t => t.type === 'Marketing Inspiration').length;
  const pendingFrenchDialogue = pending.filter(t => t.type === 'FrenchDialogueCorrection').length;
  const tabItems = [
    {
      key: 'pending',
      label: <span data-testid="tutorial:book-detail:status-tab-pending"><SyncOutlined spin={pending.length > 0} /> In-Process ({pending.length})</span>,
      children: (
        <>
          {renderSortControls('pending')}
          <div className="processing-status-list" data-tour-id="processing-status-pending-list" data-testid="tutorial:book-detail:status-pending-list">
            {pending.length === 0 ? (
              <div className="processing-status-empty">
                <Empty description="No pending tasks" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              </div>
            ) : pending.map(t => (
              <div
                key={t.lockGuid}
                className="processing-status-row processing-status-row--pending"
              >
                <div className="processing-status-row__header">
                  <Tooltip title={getEntryLanguageTooltip(t)}>
                    <span className="processing-status-row__title">{t.chapterName}</span>
                  </Tooltip>
                  <span className={`processing-status-state-pill ${isQueuedStatus(t) ? 'processing-status-state-pill--queued' : 'processing-status-state-pill--running'}`}>
                    {t.type === 'ProofreadSubmission' ? 'Preparing / Proofreading' : isQueuedStatus(t) ? 'Queued' : 'In Progress'}
                  </span>
                </div>
                <div className="processing-status-row__meta">
                  <div>
                    {getTypeTag(t.type)}{' '}
                    <small>
                      {getEntryLanguageLabel(t)}
                    </small>
                  </div>
                </div>
                {(t.blockedByCharacterAnalysis || t.statusMessage) && (
                  <div className={`processing-status-notice ${t.characterAnalysisStatus === 'failed' ? 'processing-status-notice--danger' : ''}`}>
                    <InfoCircleOutlined />
                    <span>{getCharacterAnalysisStatusMessage(t)}</span>
                  </div>
                )}
                <div className="processing-status-row__footer">
                  <div className="processing-status-time">
                    <ClockCircleOutlined /> Started {getTimeElapsed(t.timestamp)}
                  </div>
                  <div className="processing-status-estimate">
                    {Boolean(t.batchJobId) ? (
                      <Tooltip
                        title={() => {
                          const prov = t.batchProvider || 'Gemini';
                          const job = t.batchJobId || '';
                          const count = typeof t.batchInlineCount === 'number' ? t.batchInlineCount : 0;
                          return (
                            <div style={{ maxWidth: 360 }}>
                              <div><strong>Queued Translation</strong> (may take up to 24 hours)</div>
                            </div>
                          )
                        }}
                      >
                        <span className="processing-status-state-pill processing-status-state-pill--queued">Queued</span>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Estimated completion time">
                        Est. {estimatedCompletion(t)}
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ),
    },
    {
      key: 'completed',
      label: (
        <span style={{ display: 'flex', alignItems: 'center' }} data-testid="tutorial:book-detail:status-tab-completed">
          <CheckCircleOutlined style={{ marginRight: 8 }} />
          Completed ({completed.length})
        </span>
      ),
      children: (
        <>
          {renderSortControls('completed')}
          <div className="processing-status-list" data-tour-id="processing-status-completed-list" data-testid="tutorial:book-detail:status-completed-list">
            {completed.length === 0 ? (
              <div className="processing-status-empty">
                <Empty description="No completed tasks" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              </div>
            ) : completed.map(t => (
              <div
                key={t.lockGuid}
                className="processing-status-row processing-status-row--completed"
              >
                <div className="processing-status-row__header">
                  <Tooltip title={getEntryLanguageTooltip(t)}>
                    <span className="processing-status-row__title">{t.chapterName}</span>
                  </Tooltip>
                  <span className="processing-status-state-pill processing-status-state-pill--completed">Completed</span>
                </div>
                <div className="processing-status-row__meta">
                  <div>
                    {getTypeTag(t.type)}{' '}
                    <small>
                      {getEntryLanguageLabel(t)}
                    </small>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {renderExportDownload(t)}
                    <Popconfirm
                      title="Remove this notification?"
                      description="This only removes the entry from your status list. Your processed content is already saved."
                      onConfirm={() => {
                        const lockId = resolveLockIdentifier(t);
                        if (!lockId) {
                          notification.error({
                            message: 'Unable to clear',
                            description: 'Missing lock identifier for this entry.',
                            placement: 'bottomRight'
                          });
                          return;
                        }
                        clearTranslation(lockId);
                      }}
                      okText="Remove"
                      cancelText="Cancel"
                    >
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        size="small"
                        loading={clearingItem === resolveLockIdentifier(t)}
                      />
                    </Popconfirm>
                  </div>
                </div>
                <div className="processing-status-row__footer">
                  <div className="processing-status-time processing-status-time--success">
                    Completed {getTimeElapsed(t.completionTime || t.timestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ),
    },
    {
      key: 'failed',
      label: (
        <span style={{ display: 'flex', alignItems: 'center' }} data-testid="tutorial:book-detail:status-tab-failed">
          <CloseCircleOutlined style={{ marginRight: 8 }} />
          Failed ({failed.length})
        </span>
      ),
      children: (
        <>
          {renderSortControls('failed')}
          <div className="processing-status-list" data-tour-id="processing-status-failed-list" data-testid="tutorial:book-detail:status-failed-list">
            {failed.length === 0 ? (
              <div className="processing-status-empty">
                <Empty description="No failed tasks" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              </div>
            ) : failed.map(t => (
              <div
                key={t.lockGuid}
                className="processing-status-row processing-status-row--failed"
              >
                <div className="processing-status-row__header">
                  <Tooltip title={getEntryLanguageTooltip(t)}>
                    <span className="processing-status-row__title">{t.chapterName}</span>
                  </Tooltip>
                  <span className="processing-status-state-pill processing-status-state-pill--failed">Failed</span>
                </div>

                <div className="processing-status-row__meta">
                  <div>
                    {getTypeTag(t.type)}{' '}
                    <small>
                      {getEntryLanguageLabel(t)}
                    </small>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Popconfirm
                      title="Remove this notification?"
                      description="This only removes the entry from your status list."
                      onConfirm={() => {
                        const lockId = resolveLockIdentifier(t);
                        if (!lockId) {
                          notification.error({
                            message: 'Unable to clear',
                            description: 'Missing lock identifier for this entry.',
                            placement: 'bottomRight'
                          });
                          return;
                        }
                        clearTranslation(lockId);
                      }}
                      okText="Remove"
                      cancelText="Cancel"
                    >
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        size="small"
                        loading={clearingItem === resolveLockIdentifier(t)}
                      />
                    </Popconfirm>
                  </div>
                </div>

                <div className="processing-status-row__footer">
                  <div className="processing-status-time processing-status-time--danger">
                    Failed {getTimeElapsed(t.completionTime || t.timestamp)}
                  </div>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => showErrorDetails(t)}
                    icon={<InfoCircleOutlined />}
                    style={{ fontSize: 12, padding: 0 }}
                    data-testid="tutorial:book-detail:status-view-error"
                  >
                    View Error
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </>
      ),
    },
  ];

  return (
    <>
      {isVisible ? (
        <div
          data-tour-id="processing-status"
          data-testid="tutorial:book-detail:status-panel"
          className="processing-status-panel processing-status-panel--floating"
          style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}
        >
          <div className="processing-status-panel__header">
            <div>
              <div className="processing-status-title-row">
                <h2>Processing Status</h2>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Tooltip title="Take a tour">
                <Button
                  type="text"
                  size="small"
                  icon={<QuestionCircleOutlined />}
                  onClick={processingTour.start}
                  data-tour-id="processing-status-tour-trigger"
                  className="processing-status-help-button app-tour-trigger"
                  aria-label="Take a tour"
                />
              </Tooltip>
              <Button
                type="text"
                onClick={() => setIsVisible(false)}
                className="processing-status-close"
                data-testid="tutorial:book-detail:status-close"
                aria-label="Close processing status"
              >
                X
              </Button>
            </div>
          </div>
          <div className="processing-status-body">
            <Tabs
              data-tour-id="processing-status-tabs"
              activeKey={activeTab}
              onChange={key => setActiveTab(key as any)}
              centered
              style={{ width: '100%' }}
              items={tabItems}
            />

            <div className="processing-status-footer-actions" data-tour-id="processing-status-actions">
              {activeTab === 'pending' && (
                <Button
                  size="small"
                  type="link"
                  onClick={handleRefresh}
                  icon={<SyncOutlined spin={isRefreshing} />}
                  disabled={isRefreshing || refreshDisabled}
                >
                  {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </Button>
              )}
              {activeTab === 'completed' && completed.length > 0 && (
                <Button
                  size="small"
                  type="link"
                  onClick={() => setClearAllModalVisible('completed')}
                  icon={<ClearOutlined />}
                >
                  Remove all notifications
                </Button>
              )}
              {activeTab === 'failed' && failed.length > 0 && (
                <Button
                  size="small"
                  type="link"
                  onClick={() => setClearAllModalVisible('failed')}
                  icon={<ClearOutlined />}
                >
                  Clear all
                </Button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <span
          className="processing-status-trigger-tutorial-target"
          data-testid="tutorial:book-detail:status-button"
          style={{ display: 'inline-flex' }}
        >
          <Button
            data-tour-id="processing-status-trigger"
            data-testid="processing-status-trigger"
            className={`processing-status-trigger processing-status-trigger--${triggerMode}${minimalTrigger ? ' processing-status-trigger--minimal' : ''}`}
            type={triggerMode === 'inline' ? 'default' : 'primary'}
            aria-label={`Processing status${totalCount > 0 ? `, ${totalCount} items` : ''}`}
            title="Processing status"
            style={{
              ...(triggerMode === 'fixed'
                ? { position: 'fixed' as const, bottom: 20, right: 20 }
                : { position: 'static' as const }),
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '0 12px'
            }}
            onClick={() => {
              setIsVisible(true);
              if (refreshDisabled) {
                notification.info({
                  message: 'Please wait',
                  description: 'We’re preparing translations. Refresh will be available shortly.',
                  placement: 'bottomRight',
                  duration: 2,
                });
              } else {
                onRefreshImmediate(true);
              }
            }}
          >
            {minimalTrigger ? (
              <>
                {pending.length > 0 ? <SyncOutlined spin /> : <MinusCircleOutlined />}
                <span className="processing-status-trigger__label" data-testid="tutorial:book-detail:status-trigger">Status</span>
                {totalCount > 0 && <span>({totalCount})</span>}
              </>
            ) : compactTrigger ? (
              <>
                {pending.length > 0 ? <SyncOutlined spin /> : <MinusCircleOutlined />}
                Processing Status{totalCount > 0 ? ` (${totalCount})` : ''}
              </>
            ) : pending.length > 0 ? (
              <>
                <SyncOutlined spin />{' '}
                {pendingTranslations > 0 && `${pendingTranslations} Translations`}
                {pendingTranslations > 0 && (pendingProofreading > 0 || pendingEditingNotes > 0 || pendingAudiobooks > 0 || pendingExports > 0 || pendingFrenchDialogue > 0) && ', '}
                {pendingProofreading > 0 && `${pendingProofreading} Proofreads`}
                {pendingProofreading > 0 && (pendingEditingNotes > 0 || pendingAudiobooks > 0 || pendingExports > 0 || pendingFrenchDialogue > 0) && ', '}
                {pendingEditingNotes > 0 && `${pendingEditingNotes} Editing Notes`}
                {pendingEditingNotes > 0 && (pendingAudiobooks > 0 || pendingExports > 0 || pendingMarketingInspiration > 0 || pendingFrenchDialogue > 0) && ', '}
                {pendingAudiobooks > 0 && `${pendingAudiobooks} Audiobooks`}
                {pendingAudiobooks > 0 && (pendingExports > 0 || pendingMarketingInspiration > 0 || pendingFrenchDialogue > 0) && ', '}
                {pendingExports > 0 && `${pendingExports} Exports`}
                {pendingExports > 0 && (pendingMarketingInspiration > 0 || pendingFrenchDialogue > 0) && ', '}
                {pendingMarketingInspiration > 0 && `${pendingMarketingInspiration} Inspiration`}
                {pendingMarketingInspiration > 0 && pendingFrenchDialogue > 0 && ', '}
                {pendingFrenchDialogue > 0 && `${pendingFrenchDialogue} French Dialogue`}
                {' in progress'}
              </>
            ) : totalCount > 0 ? (
              <>
                <MinusCircleOutlined /> Processing Status ({totalCount})
              </>
            ) : (
              <>
                <MinusCircleOutlined /> Processing Status
              </>
            )}
          </Button>
        </span>
      )}

      {/* Error Details Modal */}
      <Modal
        title="Error Details"
        open={errorModalVisible}
        onCancel={handleCloseErrorModal}
        footer={[
          <Button key="close" onClick={handleCloseErrorModal}>
            Close
          </Button>
        ]}
      >

        <div style={{ maxHeight: 400, overflow: 'auto' }} data-tour-id="processing-status-error-details" data-testid="tutorial:book-detail:status-error-details">
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {currentError}
          </pre>
          {currentErrorLinks.length > 0 && (
            <div aria-label="Never Translate rule locations" style={{ marginBottom: 16 }}>
              {currentErrorLinks.map(link => (
                <div key={`${link.href}-${link.label}`}>
                  <a href={link.href}>{link.label}</a>
                </div>
              ))}
            </div>
          )}
          {currentCodeValue && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 500 }}>{currentCodeLabel}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <code style={{ background: 'var(--surface-muted)', padding: '4px 8px', borderRadius: 4 }}>
                  {currentCodeValue}
                </code>
                <Button size="small" type="link" onClick={handleCopySupportCode} style={{ padding: 0 }}>
                  Copy
                </Button>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                {currentCodeLabel === 'Support code'
                  ? 'Share this code with support if you need further assistance.'
                  : 'Older failed jobs may show a longer reference ID instead of a short support code.'}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Clear All Completed */}
      <Modal
        title="Clear All Completed Notifications"
        open={clearAllModalVisible === 'completed'}
        onOk={() => clearAllTranslations('completed')}
        onCancel={() => setClearAllModalVisible(null)}
        okText="Clear All"
        okButtonProps={{ loading: isClearingAll }}
        cancelText="Cancel"
      >
        <p>This will only remove these notifications from your history list.</p>
        <p>Your processed content is already saved in your book and will remain intact.</p>
        <p>This merely cleans up your notification panel.</p>
      </Modal>

      {/* Clear All Failed */}
      <Modal
        title="Clear All Failed Notifications"
        open={clearAllModalVisible === 'failed'}
        onOk={() => clearAllTranslations('failed')}
        onCancel={() => setClearAllModalVisible(null)}
        okText="Clear All"
        okButtonProps={{ loading: isClearingAll }}
        cancelText="Cancel"
      >
        <p>This will only remove these notifications from your history list.</p>
        <p>No actual content will be deleted. This merely cleans up your notification panel.</p>
      </Modal>
      <AppTour
        open={processingTour.open}
        steps={processingTourSteps}
        current={processingTour.current}
        onChange={processingTour.setCurrent}
        onClose={processingTour.close}
        onFinish={processingTour.close}
        zIndex={2400}
      />
    </>
  );
};
export default TranslationStatusPanel;
